import React, { useEffect, useRef, useState } from 'react'
import { Button, Tooltip } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import FitViewport from './FitViewport'
import InteractiveStudentPanelPreview from './InteractiveStudentPanelPreview'
import InteractiveWorkspacePreview from './InteractiveWorkspacePreview'
import { REGION_LABELS } from './previewRegions'
import { studentSurfaceFor } from '../studentSurface'
import '../../../../css/livepreview.scss'

// What a student gets for a Pop Quiz on /session/:id (Home.jsx): quiz taking is not built
// yet, and this is exactly the placeholder they see.
const PopQuizStudentView = ({ title }) => (
  <div className="lp-empty">
    <div>
      <h2 style={{ margin: '0 0 8px', color: '#111827' }}>{title || 'Pop Quiz'}</h2>
      <p style={{ margin: 0 }}>This quiz isn&apos;t open for students yet.</p>
    </div>
  </div>
)

const sameBoxes = (a, b) =>
  a.length === b.length &&
  a.every((box, i) => {
    const other = b[i]
    return (
      box.id === other.id &&
      Math.abs(box.top - other.top) < 0.5 &&
      Math.abs(box.left - other.left) < 0.5 &&
      Math.abs(box.width - other.width) < 0.5 &&
      Math.abs(box.height - other.height) < 0.5
    )
  })

// Finds the regions the previews marked active (data-lp-active) and measures where they
// are on screen, relative to `container`. The highlight is drawn as boxes in an overlay
// layer above the preview rather than as CSS on the region elements: the regions are a
// mix of static, relative and absolutely positioned panes, many fully covered by opaque
// children (CodeMirror, Blockly, iframes), and styling them directly either hid the
// highlight underneath or moved panes around.
const useActiveRegionBoxes = (containerRef, deps) => {
  const [boxes, setBoxes] = useState([])
  // Active regions that exist on the page at all, drawn or not (scrolled out of view).
  const [present, setPresent] = useState([])
  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined
    let frame = null
    const measure = () => {
      frame = null
      const bounds = container.getBoundingClientRect()
      const next = []
      const found = new Set()
      container.querySelectorAll('[data-lp-active="true"]').forEach((el) => {
        const id = el.getAttribute('data-lp-region')
        found.add(id)
        const rect = el.getBoundingClientRect()
        // Clip to the visible preview area and to every scrolling pane the region sits in,
        // so a box never floats over a neighbouring pane when its own pane is scrolled.
        let top = Math.max(rect.top, bounds.top)
        let left = Math.max(rect.left, bounds.left)
        let bottom = Math.min(rect.bottom, bounds.bottom)
        let right = Math.min(rect.right, bounds.right)
        for (let node = el.parentElement; node && node !== container; node = node.parentElement) {
          const style = window.getComputedStyle(node)
          const clipsY = style.overflowY !== 'visible'
          const clipsX = style.overflowX !== 'visible'
          if (!clipsX && !clipsY) continue
          const box = node.getBoundingClientRect()
          if (clipsY) {
            top = Math.max(top, box.top)
            bottom = Math.min(bottom, box.bottom)
          }
          if (clipsX) {
            left = Math.max(left, box.left)
            right = Math.min(right, box.right)
          }
        }
        if (bottom - top < 4 || right - left < 4) return
        next.push({ id, top: top - bounds.top, left: left - bounds.left, width: right - left, height: bottom - top })
      })
      setBoxes((prev) => (sameBoxes(prev, next) ? prev : next))
      const foundIds = [...found].sort()
      setPresent((prev) => (prev.join(',') === foundIds.join(',') ? prev : foundIds))
    }
    const schedule = () => {
      if (frame == null) frame = requestAnimationFrame(measure)
    }
    schedule()
    // Layout changes from every direction: the panel resizing, panes scrolling, the
    // previews switching which element is active, and their own draggable dividers. The
    // interval is the catch-all for layout shifts no observer reports.
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    resizeObserver?.observe(container)
    const mutationObserver = new MutationObserver(schedule)
    mutationObserver.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-lp-active', 'style', 'class'],
    })
    container.addEventListener('scroll', schedule, true)
    const interval = setInterval(schedule, 400)
    return () => {
      if (frame != null) cancelAnimationFrame(frame)
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
      container.removeEventListener('scroll', schedule, true)
      clearInterval(interval)
    }
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps
  return { boxes, present }
}

// The creation wizard's right column: the student's screen for the session being built,
// live and interactive - type code, run it, chat with the assistant - with the part the
// current step configures highlighted. Nothing done here is saved; Reset starts the
// student side over.
//
// `session` is the wizard's merged draft (its saved row plus unsaved edits); it has no id
// until the Language step creates the row, and the AI chat (and C++/SQL runs) need one.
// `highlight` lists region ids from previewRegions.js, or ['all'] for the whole view.
// `headerExtra` is an optional control for the panel's header (the experiment wizard's
// "Preview as" condition picker).
const LiveStudentPreview = ({
  session,
  testsEnabled,
  chatEnabled,
  highlight = [],
  resetToken,
  onReset,
  onBeforeChat,
  headerExtra = null,
}) => {
  const bodyRef = useRef(null)
  const surface = session?.type ? studentSurfaceFor(session) : null
  const highlightsAll = highlight.includes('all')
  const { boxes, present } = useActiveRegionBoxes(bodyRef, [surface, highlight.join(',')])

  // Name what the step configures, but only what the layout actually has: some layouts have
  // no place for a region (JSBin shows students no task; the Initial/Final exercises have
  // no test list). A region that exists but is scrolled out of its pane says so.
  const drawnIds = new Set(boxes.map((box) => box.id))
  let focusLabel = ''
  if (highlightsAll) focusLabel = REGION_LABELS.all
  else if (present.length) {
    focusLabel = highlight
      .filter((id) => present.includes(id))
      .map((id) => (drawnIds.has(id) ? REGION_LABELS[id] : `${REGION_LABELS[id]} (scroll to see)`))
      .join(' · ')
  } else if (highlight.length && surface && surface !== 'quiz') {
    focusLabel = `${highlight.map((id) => REGION_LABELS[id]).filter(Boolean).join(' · ')}: not part of this student view`
  }

  let body
  if (!surface) {
    body = <div className="lp-empty">Pick a language to see what students will get.</div>
  } else if (surface === 'quiz') {
    body = <PopQuizStudentView title={session.task || session.subject} />
  } else {
    const shared = { session, testsEnabled, chatEnabled, highlight, resetToken, onBeforeChat }
    let view = null
    // 'workspace' covers the web exercises and the Python family (/workspace/:id).
    if (surface === 'workspace') view = <InteractiveWorkspacePreview {...shared} />
    else if (surface === 'studentPanel') view = <InteractiveStudentPanelPreview {...shared} />
    body = view ? (
      <FitViewport minWidth={1100}>{view}</FitViewport>
    ) : (
      <div className="lp-empty">No student preview for this session type.</div>
    )
  }

  // What only starts working once the session row exists: the AI chat everywhere, and Run
  // for the languages compiled on the server (C++, SQL). Python and web checks run in the
  // browser, so they work from the start.
  const serverRun = session?.type === 'CPP' || String(session?.type || '').startsWith('SQL')
  const needsSession = surface && surface !== 'quiz' && !session?.id

  // One short line next to the title, rather than its own row.
  let note = 'Try it as a student. Nothing is saved.'
  if (needsSession) {
    note = serverRun ? 'Run and chat work after the Language step.' : 'Chat works after the Language step.'
  }

  return (
    <section className="lp-panel" aria-label="Student view preview">
      <div className="lp-panel__head">
        <span className="lp-panel__title">Student view</span>
        <span className="lp-panel__badge">Preview</span>
        {surface && surface !== 'quiz' && (
          <span className="lp-panel__note" title={note}>
            {note}
          </span>
        )}
        {focusLabel && (
          <span className="lp-panel__focus" title="The part of the student view this step configures">
            Highlighted: {focusLabel}
          </span>
        )}
        {headerExtra && (
          <span className="lp-panel__extra" style={{ marginLeft: focusLabel ? 0 : 'auto' }}>
            {headerExtra}
          </span>
        )}
        {surface && surface !== 'quiz' && (
          <Tooltip title="Clear what you typed and ran in the preview">
            <Button
              size="small"
              startIcon={<RestartAltIcon fontSize="small" />}
              onClick={onReset}
              sx={{ textTransform: 'none', ml: focusLabel || headerExtra ? 0 : 'auto', flexShrink: 0 }}>
              Reset
            </Button>
          </Tooltip>
        )}
      </div>
      <div className="lp-panel__body" ref={bodyRef}>
        {body}
        <div className="lp-overlay" aria-hidden="true">
          {highlightsAll && <div className="lp-box lp-box--all" key={`all-${surface}`} />}
          {boxes.map((box, index) => (
            <div
              key={`${box.id}-${index}`}
              className="lp-box"
              style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default LiveStudentPreview
